import { memo } from "react";
import { Edit2, Eye, IndianRupee, Calendar, Clock, Lock } from "lucide-react";
import { SubscriptionPlan } from "@repo/types";

export const SubscriptionTableRow = memo(
  ({
    plan,
    onEdit,
    onViewDetails,
    view = "table",
  }: {
    plan: SubscriptionPlan;
    onEdit: (plan: SubscriptionPlan) => void;
    onViewDetails: (plan: SubscriptionPlan) => void;
    view?: "table" | "card";
  }) => {
    if (view === "card") {
      return (
        <div className="bg-white dark:bg-[#111c44] rounded-2xl p-5 shadow-sm border border-slate-200 dark:border-white/10 hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300 group">
          <div className="flex items-start justify-between mb-5">
            <div className="flex gap-3">
              <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-[#1b254b] border border-slate-200 dark:border-white/5 shrink-0 flex items-center justify-center text-slate-500 dark:text-slate-400 font-bold overflow-hidden shadow-sm">
                <span className="text-lg">
                  {plan.PlanName?.charAt(0).toUpperCase() || "S"}
                </span>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {plan.PlanName}
                </h3>
                <div className="mt-1">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-[#1b254b] px-2 py-0.5 rounded-md uppercase tracking-wide border border-slate-200 dark:border-white/5">
                    <IndianRupee size={10} />
                    {plan.Price}
                  </span>
                </div>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide ${
                plan.IsActive
                  ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/40"
                  : "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/40"
              }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full ${plan.IsActive ? "bg-emerald-500" : "bg-rose-500"}`}
              />
              {plan.IsActive ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="space-y-3 mb-5">
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100 dark:border-white/10">
              <div className="space-y-2.5">
                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                  {plan.IsLifetime ? (
                    <Lock
                      size={14}
                      className="text-slate-400 dark:text-slate-500"
                    />
                  ) : (
                    <Calendar
                      size={14}
                      className="text-slate-400 dark:text-slate-500"
                    />
                  )}
                  {plan.IsLifetime
                    ? "Lifetime Access"
                    : `${plan.DurationMonths} Months`}
                </div>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                  <Clock
                    size={14}
                    className="text-slate-400 dark:text-slate-500"
                  />
                  <span>
                    Created: {plan.CreatedAt ? new Date(plan.CreatedAt).toLocaleDateString() : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onViewDetails(plan)}
              className="flex-1 h-10 bg-white dark:bg-[#1b254b] border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-[#111c44] text-slate-700 dark:text-slate-200 rounded-xl flex items-center justify-center gap-2 text-xs font-semibold transition-all shadow-sm"
            >
              <Eye size={16} />
              View
            </button>
            <button
              onClick={() => onEdit(plan)}
              className="flex-1 h-10 bg-white dark:bg-[#1b254b] border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-[#111c44] text-slate-700 dark:text-slate-200 rounded-xl flex items-center justify-center gap-2 text-xs font-semibold transition-all shadow-sm"
            >
              <Edit2 size={16} />
              Edit
            </button>
          </div>
        </div>
      );
    }

    return (
      <tr className="hover:bg-slate-50/50 dark:hover:bg-[#1b254b]/30 transition-colors group border-b border-slate-100 dark:border-white/10 last:border-none">
        {/* Plan Details */}
        <td className="px-6 py-4 align-middle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-[#1b254b] border border-slate-200 dark:border-white/5 shrink-0 flex items-center justify-center text-slate-500 dark:text-slate-400 font-bold overflow-hidden">
              <span className="text-sm tracking-widest uppercase">
                {plan.PlanName?.charAt(0) || "S"}
              </span>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-tight mb-0.5">
                {plan.PlanName}
              </p>
            </div>
          </div>
        </td>

        {/* Duration / Coverage */}
        <td className="px-6 py-4 align-middle">
          <div className="flex flex-col gap-1 text-sm text-slate-700 dark:text-slate-300 font-medium">
            <div className="flex items-center gap-1.5">
              {plan.IsLifetime ? (
                <Lock
                  size={14}
                  className="text-slate-400 dark:text-slate-500"
                />
              ) : (
                <Calendar
                  size={14}
                  className="text-slate-400 dark:text-slate-500"
                />
              )}
              <span>
                {plan.IsLifetime ? "Lifetime" : `${plan.DurationMonths} Months`}
              </span>
            </div>
          </div>
        </td>

        {/* Pricing */}
        <td className="px-6 py-4 align-middle">
          <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 font-bold">
            <IndianRupee
              size={14}
              className="text-slate-500 dark:text-slate-400"
            />
            {plan.Price}
          </div>
        </td>

        {/* Status */}
        <td className="px-6 py-4 align-middle">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide w-fit ${
              plan.IsActive
                ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/40"
                : "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/40"
            }`}
          >
            <div
              className={`w-1.5 h-1.5 rounded-full ${plan.IsActive ? "bg-emerald-500" : "bg-rose-500"}`}
            />
            {plan.IsActive ? "Active" : "Inactive"}
          </span>
        </td>

        {/* Actions */}
        <td className="px-6 py-4 align-middle text-center">
          <div className="flex justify-center gap-2 transition-opacity">
            <button
              onClick={() => onViewDetails(plan)}
              className="p-2 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
              title="View Details"
            >
              <Eye size={18} />
            </button>
            <button
              onClick={() => onEdit(plan)}
              className="p-2 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
              title="Edit Plan"
            >
              <Edit2 size={18} />
            </button>
          </div>
        </td>
      </tr>
    );
  },
);
